import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import { UserContext } from "app/contexts/UserContext";
import Setup from "../Setup";
import Offerings from "../Offerings";
import Customers from "../Customers";
import Navbar from "app/components/Navbar";
import Tabs from "app/components/Tabs";
import { dismissSetupRail } from "app/components/AcademySetup/SetupToast";
import styles from "./styles.module.scss";

export default function FacilityBilling() {
  const { t } = useTranslation();
  const facility = useCurrentFacility();
  const history = useHistory();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(0);
  // The tabs are local state, not routes, so the sidebar's route watcher
  // never sees a switch; spec §4 still counts it as leaving the view. The
  // Setup tab's Stripe connect can show the rail here as well as on /billing.
  useEffect(() => {
    dismissSetupRail();
  }, [selectedTab]);
  const [stripeId, setStripeId] = useState<string | null>(null);
  const [activatedAccount, setActivatedAccount] = useState<boolean>(false);
  const [enabledIntegration, setEnabledIntegration] = useState<boolean>(false);
  const user = useContext(UserContext);
  const showCustomers = enabledIntegration || user.behavesLikeAdmin;

  // Check feature flag - redirect if not enabled
  useEffect(() => {
    if (!facility.pending && !facility.data?.hasFacilityLevelStripe) {
      history.replace("/");
    }
  }, [facility.pending, facility.data, history]);

  // Initialize stripe data from facility hook
  useEffect(() => {
    if (facility.data) {
      setStripeId(facility.data.stripeAccountId || null);
      setEnabledIntegration(facility.data.stripeIntegrationEnabled || false);
      setActivatedAccount(facility.data.stripeAccountActive || false);
    }
  }, [facility.data]);

  // Handle URL query param for tab selection
  const setupTabIndex = showCustomers ? 2 : 1;
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get("tab");
    if (tab === "setup") {
      setSelectedTab(setupTabIndex);
    } else if (tab === "customers" && showCustomers) {
      setSelectedTab(1);
    } else if (tab === "offerings") {
      setSelectedTab(0);
    }
  }, [location.search, showCustomers, setupTabIndex]);

  // Don't render anything until facility data is loaded
  // This prevents Setup from using the wrong API endpoint during OAuth callback
  if (facility.pending || !facility.data?.id) {
    return null;
  }

  // Build API paths for facility
  const facilityId = facility.data.id;
  const offeringsApiBasePath = `facilities/${facilityId}/stripe_products`;
  const setupApiBasePath = `facilities/${facilityId}/stripe_connect`;
  const customersApiPath = `/facilities/${facilityId}/stripe_purchases/customers`;

  // Build tabs array - Offerings and Setup always shown, Customers for admins or with Stripe
  const tabs = [];
  tabs.push({
    label: t("Offerings"),
    onClickHandler: () => setSelectedTab(0),
    isActive: selectedTab === 0,
  });
  if (showCustomers) {
    tabs.push({
      label: t("Customers"),
      onClickHandler: () => setSelectedTab(1),
      isActive: selectedTab === 1,
    });
  }
  tabs.push({
    label: t("Setup"),
    onClickHandler: () => setSelectedTab(setupTabIndex),
    isActive: selectedTab === setupTabIndex,
  });

  const renderTab = () => {
    if (selectedTab === 0) {
      return (
        <Offerings
          enabledIntegration={enabledIntegration}
          apiBasePath={offeringsApiBasePath}
          facilityId={facilityId}
          isLoadingData={facility.pending}
        />
      );
    }

    if (selectedTab === 1 && showCustomers) {
      return (
        <Customers
          stripeIntegrationEnabled={enabledIntegration}
          apiPath={customersApiPath}
          basePath="/facility_billing"
          facilityId={facilityId}
        />
      );
    }

    return (
      <Setup
        isLoadingData={facility.pending}
        stripeId={stripeId}
        setStripeId={setStripeId}
        activatedAccount={activatedAccount}
        enabledIntegration={enabledIntegration}
        setEnabledIntegration={setEnabledIntegration}
        apiBasePath={setupApiBasePath}
        facilityId={facilityId}
      />
    );
  };

  return (
    <div className={styles.container}>
      <Navbar label={t("Facility Billing")} hasSearch={false} hasNotifications={false} />
      <Tabs tabs={tabs} />
      <div className={`${styles.content} billing-page-container`}>
        {renderTab()}
      </div>
    </div>
  );
}
